// UserEnrollment data model interface
// This will be converted to TypeScript in the future

/**
 * UserEnrollment interface definition
 * @typedef {Object} UserEnrollment
 * @property {string} user_email - User identifier (partition key)
 * @property {string} program_name - Program identifier (sort key)
 * @property {string} enrollment_date - Enrollment timestamp
 * @property {'active'|'inactive'|'completed'} status - Enrollment status
 * @property {Object} [progress] - Optional progress tracking
 * @property {string[]} [progress.completed_assessments] - Completed assessments
 * @property {string} [progress.current_assessment] - Current assessment
 * @property {string} [progress.last_activity] - Last activity timestamp
 */

/**
 * Validate user enrollment data
 * @param {UserEnrollment} enrollment - Enrollment object to validate
 * @returns {{isValid: boolean, errors: Object}} Validation result
 */
export const validateUserEnrollment = (enrollment) => {
  const errors = {};

  if (!enrollment.user_email || typeof enrollment.user_email !== 'string') {
    errors.user_email = 'User email is required';
  } else {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(enrollment.user_email)) {
      errors.user_email = 'Valid email address is required';
    }
  }

  if (!enrollment.program_name || typeof enrollment.program_name !== 'string') {
    errors.program_name = 'Program name is required';
  }

  if (!enrollment.enrollment_date || typeof enrollment.enrollment_date !== 'string') {
    errors.enrollment_date = 'Enrollment date is required';
  }

  if (enrollment.status && !['active', 'inactive', 'completed'].includes(enrollment.status)) {
    errors.status = 'Status must be active, inactive, or completed';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Create a new user enrollment object with default values
 * @param {Partial<UserEnrollment>} enrollmentData - Partial enrollment data
 * @returns {UserEnrollment} Complete enrollment object
 */
export const createUserEnrollment = (enrollmentData = {}) => {
  return {
    user_email: '',
    program_name: '',
    enrollment_date: new Date().toISOString(),
    status: 'active',
    ...enrollmentData
  };
};

/**
 * Check if user is enrolled in a program
 * @param {UserEnrollment[]} enrollments - Array of user enrollments
 * @param {string} programName - Program name to check
 * @returns {boolean} True if user is enrolled
 */
export const isUserEnrolled = (enrollments, programName) => {
  return enrollments.some(enrollment => 
    enrollment.program_name === programName && 
    enrollment.status === 'active'
  );
};

/**
 * Get user's enrollment status for a program
 * @param {UserEnrollment[]} enrollments - Array of user enrollments
 * @param {string} programName - Program name to check
 * @returns {string|null} Enrollment status or null if not enrolled
 */
export const getUserEnrollmentStatus = (enrollments, programName) => {
  const enrollment = enrollments.find(e => e.program_name === programName);
  return enrollment ? enrollment.status : null;
};